/* eslint-disable import/no-cycle */
import {action, decorate, observable} from 'mobx';
import {Api} from '../middleware/api';
import {Actions, EntityStore, LoadStrategies} from './entityStore';
import {SensorData} from '../models/sensorData';
import {sensorDataAnalysisEndpoint} from '../middleware/endpoints/sensorData';
import {sortChronologically} from '../components/shared/tables/sorters';
import {round} from '../utils/number';
/**
 *
 * @param {Array<SensorData} sensorData
 * @param {number} offset
 * @returns Return series data of actual line
 */
const convertToActualData = (sensorData, offset) => {
  const actualData = [];
  sensorData.forEach((sensor, index) => {
    const timestamp = new Date(sensor.timestamp).getTime() + offset * 1000;
    if (!index) {
      actualData.push([timestamp, round(sensor.value, 2)]);
    } else {
      const prevEntry = actualData[actualData.length - 1];
      const quantity = sensor.value + prevEntry[1];

      actualData.push([timestamp, round(quantity, 2)]);
    }
  });

  return actualData;
};

export class SensorDataStore extends EntityStore {
  sensorData = [];
  identityField = 'sensorName';

  constructor(rootStore) {
    super(rootStore, 'sensorData', Api.sensorData, SensorData);
  }

  loadSensorDataForAnalysis({sensorName, workplaceId, fromDate, toDate, orderId, materialId}) {
    return this.loadAll({
      apiEndpoint: sensorDataAnalysisEndpoint,
      params: {
        sensorName,
        workplaceId,
        fromDate: fromDate?.toISOString(),
        toDate: toDate?.toISOString(),
        orderId,
        materialId,
      },
      raw: true,
    });
  }

  loadLatestByWorkplace(sensorName, workplaceId) {
    return this.loadAll({
      params: {
        sensorName,
        workplaceId,
        fromDate: new Date(),
        toDate: new Date(),
        amountOfLeadingItems: 2,
      },
    });
  }

  loadByWorkplaceAndTimeRange(sensorName, workplaceId, fromDate, toDate) {
    return this.loadAll({
      params: {
        sensorName,
        workplaceId,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        amountOfLeadingItems: 1,
      },
      strategy: LoadStrategies.replace,
    });
  }

  getLatestByWorkplace(sensorName, workplaceId) {
    const data = this.sensorData
      .filter((sd) => sd.sensorName === sensorName && sd.workplaceId === workplaceId)
      .sort((a, b) => sortChronologically(b.timestamp, a.timestamp));
    return data.length > 0 ? data[0] : null;
  }

  getDataByWorkplaceId(sensorName, workplaceId) {
    return this.sensorData
      .filter((sd) => sd.sensorName === sensorName && sd.workplaceId === workplaceId)
      .sort((a, b) => sortChronologically(a.timestamp, b.timestamp));
  }

  getActualDataBySensorAndWorkplaceId(params, sensors, orderId, offset = 0) {
    if (!sensors[0]) {
      return [];
    }
    return this.api[Actions.loadAll]({
      ...params,
      sensorName: sensors[0],
    })
      .then((response) => {
        const data = response.data || [];
        const sensorData = data
          .filter((sd) => sd.orderId === orderId)
          .sort((a, b) => sortChronologically(a.timestamp, b.timestamp));

        if (sensorData.length) {
          return convertToActualData(sensorData, offset);
        }

        sensors.splice(0, 1);

        return this.getActualDataBySensorAndWorkplaceId(params, sensors, orderId, offset);
      })
      .catch((e) => this.handleApiError(e, Actions.loadAll));
  }
}

decorate(SensorDataStore, {
  sensorData: observable,
  loadSensorDataForAnalysis: action,
});
